import React from "react";
import { FormSection, InputField } from "../../../common/FormComponents";
import {
  Button,
  IconButton,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

type SponsorOption = {
  id: string;
  name: string;
  price: string;
  benefits: string;
};

type Sponsorships = {
  presentingSponsor: {
    available: boolean;
    price: string;
    benefits: string;
  };
  featuredSponsors: {
    available: boolean;
    price: string;
    benefits: string;
    options: SponsorOption[];
  };
  supportingSponsors: {
    available: boolean;
    price: string;
    benefits: string;
  };
};

type SponsorshipSectionProps = {
  sponsorships: Sponsorships;
  onPresentingSponsorChange: ({
    field,
    value,
  }: {
    field: keyof Sponsorships["presentingSponsor"];
    value: string | boolean;
  }) => void;
  onFeaturedSponsorsChange: ({
    field,
    value,
  }: {
    field: keyof Omit<Sponsorships["featuredSponsors"], "options">;
    value: string | boolean;
  }) => void;
  onSupportingSponsorsChange: ({
    field,
    value,
  }: {
    field: keyof Sponsorships["supportingSponsors"];
    value: string | boolean;
  }) => void;
  onAddSponsorOption: () => void;
  onUpdateSponsorOption: ({
    id,
    field,
    value,
  }: {
    id: string;
    field: keyof SponsorOption;
    value: string;
  }) => void;
  onRemoveSponsorOption: ({ id }: { id: string }) => void;
  errors?: Record<string, string>;
};

const SponsorshipSection = ({
  sponsorships,
  onPresentingSponsorChange,
  onFeaturedSponsorsChange,
  onSupportingSponsorsChange,
  onAddSponsorOption,
  onUpdateSponsorOption,
  onRemoveSponsorOption,
  errors = {},
}: SponsorshipSectionProps) => {
  const handlePresentingSponsorTextChange =
    (field: keyof Omit<Sponsorships["presentingSponsor"], "available">) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onPresentingSponsorChange({ field, value: e.target.value });
    };

  const handleFeaturedSponsorsTextChange =
    (
      field: keyof Omit<
        Sponsorships["featuredSponsors"],
        "available" | "options"
      >
    ) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onFeaturedSponsorsChange({ field, value: e.target.value });
    };

  const handleSupportingSponsorsTextChange =
    (field: keyof Omit<Sponsorships["supportingSponsors"], "available">) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onSupportingSponsorsChange({ field, value: e.target.value });
    };

  const handlePresentingSponsorAvailableChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    onPresentingSponsorChange({ field: "available", value: e.target.checked });
  };

  const handleFeaturedSponsorsAvailableChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    onFeaturedSponsorsChange({ field: "available", value: e.target.checked });
  };

  const handleSupportingSponsorsAvailableChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    onSupportingSponsorsChange({ field: "available", value: e.target.checked });
  };

  const handleSponsorOptionChange =
    (id: string, field: keyof SponsorOption) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onUpdateSponsorOption({ id, field, value: e.target.value });
    };

  return (
    <FormSection
      title="Sponsorship Opportunities"
      tooltipText="Set up sponsorship options for your event"
    >
      {/* Presenting Sponsor */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <Typography
            variant="h6"
            component="h3"
            className="text-lg font-semibold"
          >
            Presenting Sponsor
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={sponsorships.presentingSponsor.available}
                onChange={handlePresentingSponsorAvailableChange}
                color="primary"
              />
            }
            label="Available"
          />
        </div>

        {sponsorships.presentingSponsor.available && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              id="presenting-sponsor-price"
              name="price"
              label="Price ($)"
              value={sponsorships.presentingSponsor.price}
              onChange={handlePresentingSponsorTextChange("price")}
              error={!!errors.presentingSponsorPrice}
              helperText={errors.presentingSponsorPrice || ""}
              required
              tooltipText="The price for the presenting sponsor package"
            />
            <div className="md:col-span-2">
              <InputField
                id="presenting-sponsor-benefits"
                name="benefits"
                label="Benefits"
                value={sponsorships.presentingSponsor.benefits}
                onChange={handlePresentingSponsorTextChange("benefits")}
                error={!!errors.presentingSponsorBenefits}
                helperText={errors.presentingSponsorBenefits || ""}
                multiline
                rows={3}
                required
                tooltipText="List the benefits included with the presenting sponsor package"
              />
            </div>
          </div>
        )}
      </div>

      <Divider className="my-6" />

      {/* Featured Sponsors */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <Typography
            variant="h6"
            component="h3"
            className="text-lg font-semibold"
          >
            Featured Sponsors
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={sponsorships.featuredSponsors.available}
                onChange={handleFeaturedSponsorsAvailableChange}
                color="primary"
              />
            }
            label="Available"
          />
        </div>

        {sponsorships.featuredSponsors.available && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <InputField
                id="featured-sponsors-price"
                name="price"
                label="Default Price ($)"
                value={sponsorships.featuredSponsors.price}
                onChange={handleFeaturedSponsorsTextChange("price")}
                error={!!errors.featuredSponsorsPrice}
                helperText={errors.featuredSponsorsPrice || ""}
                required
                tooltipText="The default price for featured sponsor packages"
              />
              <div className="md:col-span-2">
                <InputField
                  id="featured-sponsors-benefits"
                  name="benefits"
                  label="Default Benefits"
                  value={sponsorships.featuredSponsors.benefits}
                  onChange={handleFeaturedSponsorsTextChange("benefits")}
                  error={!!errors.featuredSponsorsBenefits}
                  helperText={errors.featuredSponsorsBenefits || ""}
                  multiline
                  rows={3}
                  required
                  tooltipText="Default benefits included with featured sponsor packages"
                />
              </div>
            </div>

            <div className="mt-4">
              <div className="flex justify-between items-center mb-2">
                <Typography
                  variant="subtitle1"
                  component="h4"
                  className="font-medium"
                >
                  Featured Sponsor Packages
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={onAddSponsorOption}
                  size="small"
                  sx={{
                    borderColor: "#E1A731",
                    color: "#E1A731",
                    "&:hover": {
                      borderColor: "#c78f1e",
                      backgroundColor: "rgba(225, 167, 49, 0.04)",
                    },
                  }}
                >
                  Add Package
                </Button>
              </div>

              <div className="space-y-4">
                {sponsorships.featuredSponsors.options.map((option) => (
                  <Card
                    key={option.id}
                    variant="outlined"
                    className="border border-gray-200"
                  >
                    <CardContent>
                      <div className="flex justify-between items-start mb-2">
                        <Typography
                          variant="subtitle1"
                          component="h5"
                          className="font-semibold"
                        >
                          {option.name || "New Sponsor Package"}
                        </Typography>
                        <IconButton
                          aria-label="delete"
                          size="small"
                          onClick={() =>
                            onRemoveSponsorOption({ id: option.id })
                          }
                          sx={{ color: "#ef4444" }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <InputField
                          id={`sponsor-name-${option.id}`}
                          name="name"
                          label="Package Name"
                          value={option.name}
                          onChange={handleSponsorOptionChange(
                            option.id,
                            "name"
                          )}
                          error={!!errors[`sponsorName-${option.id}`]}
                          helperText={errors[`sponsorName-${option.id}`] || ""}
                          required
                          tooltipText="Name for this sponsor package"
                        />

                        <InputField
                          id={`sponsor-price-${option.id}`}
                          name="price"
                          label="Price ($)"
                          value={option.price}
                          onChange={handleSponsorOptionChange(
                            option.id,
                            "price"
                          )}
                          error={!!errors[`sponsorPrice-${option.id}`]}
                          helperText={errors[`sponsorPrice-${option.id}`] || ""}
                          required
                          tooltipText="Price for this sponsor package"
                        />

                        <div className="md:col-span-2">
                          <InputField
                            id={`sponsor-benefits-${option.id}`}
                            name="benefits"
                            label="Benefits"
                            value={option.benefits}
                            onChange={handleSponsorOptionChange(
                              option.id,
                              "benefits"
                            )}
                            error={!!errors[`sponsorBenefits-${option.id}`]}
                            helperText={
                              errors[`sponsorBenefits-${option.id}`] || ""
                            }
                            multiline
                            rows={2}
                            required
                            tooltipText="Benefits included with this sponsor package"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      <Divider className="my-6" />

      {/* Supporting Sponsors */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <Typography
            variant="h6"
            component="h3"
            className="text-lg font-semibold"
          >
            Supporting Sponsors
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={sponsorships.supportingSponsors.available}
                onChange={handleSupportingSponsorsAvailableChange}
                color="primary"
              />
            }
            label="Available"
          />
        </div>

        {sponsorships.supportingSponsors.available && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              id="supporting-sponsors-price"
              name="price"
              label="Price ($)"
              value={sponsorships.supportingSponsors.price}
              onChange={handleSupportingSponsorsTextChange("price")}
              error={!!errors.supportingSponsorsPrice}
              helperText={errors.supportingSponsorsPrice || ""}
              required
              tooltipText="The price for supporting sponsor packages"
            />
            <div className="md:col-span-2">
              <InputField
                id="supporting-sponsors-benefits"
                name="benefits"
                label="Benefits"
                value={sponsorships.supportingSponsors.benefits}
                onChange={handleSupportingSponsorsTextChange("benefits")}
                error={!!errors.supportingSponsorsBenefits}
                helperText={errors.supportingSponsorsBenefits || ""}
                multiline
                rows={3}
                required
                tooltipText="Benefits included with supporting sponsor packages"
              />
            </div>
          </div>
        )}
      </div>
    </FormSection>
  );
};

export default SponsorshipSection;
